import { useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useUserStore } from '@/stores/userStore';
import { isWhiteListed } from './config';
import { toast } from 'sonner';
import { cancelManager } from '@/api';
import { getHasPermissionRoutes, routes } from './routes';
import { matchRouteConfig } from './utils';

interface AuthGuardProps {
  children: React.ReactNode;
}

/**
 * 路由守卫组件
 * 负责路由跳转前的权限校验
 */
export function AuthGuard({ children }: AuthGuardProps) {
  const location = useLocation();
  const navigate = useNavigate();
  const { token, userInfo } = useUserStore();
  const lastNotLoggedInPathRef = useRef<string | null>(null);

  useEffect(() => {
    const validateRoute = async () => {
      const currentPath = location.pathname;

      // 使用 window.location.search 获取 hash 之前的查询参数
      const urlSearchParams = new URLSearchParams(window.location.search);
      const state = urlSearchParams.get('state');
      // 检查是否是第三方回跳（优先处理）
      if (state === 'playlet') {
        const queryString = urlSearchParams.toString();
        const targetPath = `/playlet${queryString ? `?${queryString}` : ''}`;
        console.log(targetPath, 'targetPath');
        navigate(targetPath, { replace: true });
        return;
      }

      if (token) {
        lastNotLoggedInPathRef.current = null;
      }

      const knownRoute = matchRouteConfig(currentPath, routes);
      if (knownRoute?.meta?.requireAuth === false) {
        return;
      }

      // 白名单路径直接放行
      if (isWhiteListed(currentPath)) {
        return;
      }

      // 未登录用户重定向到登录页
      if (!token) {
        if (lastNotLoggedInPathRef.current !== currentPath) {
          lastNotLoggedInPathRef.current = currentPath;
          toast.error('当前未登录，请先登录');
        }
        navigate('/login', {
          replace: true,
          state: { from: currentPath },
        });
        return;
      }

      if (userInfo?.permissionCodes?.length === 0) {
        toast.error('您没有权限访问本系统，请联系管理员');
        navigate('/login', {
          replace: true,
          state: { from: currentPath },
        });
        return;
      }

      if (!knownRoute) {
        console.warn('未知的路由路径,重定向到/404', currentPath, routes);
        navigate('/404', { replace: true });
        return;
      }

      const permissionCodes = userInfo?.permissionCodes ?? [];
      const authorizedRoutes = getHasPermissionRoutes(permissionCodes);
      const authorizedRoute = matchRouteConfig(currentPath, authorizedRoutes);

      if (!authorizedRoute) {
        toast.error('暂无权限访问');
        navigate('/404', { replace: true });
      }
    };

    validateRoute();
  }, [location.pathname, location.search, token, userInfo?.permissionCodes, navigate]);

  useEffect(() => {
    return () => {
      //非根路由重定向时(避免重定向目标页面的初始请求被取消)，取消所有请求
      if (location.pathname !== '/') {
        console.log(
          `[AuthGuard]页面切换，调用：cancelAllRequests，取消在${location.pathname}页面时发起但未完成的请求`
        );
        cancelManager.cancelAllRequests();
      }
    };
  }, [location.pathname]);

  return <>{children}</>;
}
